
import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Button,
} from "@mui/material";
import moment from "moment";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getPropertyGroups } from "../../property/api/actions";
import MUITable from "../../../components/MUITable";


function PropertyGroupTable(props) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useState({
    active: 'true',
    isRemote: '',
    search: '',
    page: 1,
    limit: 10,
  });
  
  const [activeFilter, setActiveFilter] = useState('true'); // Default to Active
  const [remoteFilter, setRemoteFilter] = useState(null);

  const total = props.properties?.propertyGroups?.total || 0;
  const currentPage = props.properties?.propertyGroups?.page || 1;
  const currentLimit = props.properties?.propertyGroups?.limit || 10;

  useEffect(() => {
    props.getPropertyGroups(searchParams);
  }, [searchParams]);

  const handleSearch = (params) => {
    const newParams = {
      ...searchParams, // Preserve existing filters
      ...params, // Override with new search params
      page: 1, // Reset to first page when searching
      limit: params.limit || searchParams.limit || 10,
    };
    setSearchParams(newParams);
    props.getPropertyGroups(newParams);
  };

  const handleFilterChange = (filterKey, value, params) => {
    const newParams = {
      ...params,
      page: 1, // Reset to first page when filter changes
    };
    setSearchParams(newParams);
    props.getPropertyGroups(newParams);
  };


  const handleGroupChipClick = (group) => {
    const groupId = group.id || group.uuid;
    navigate(`/disconnect-management?groupId=${groupId}`);
  };

  const handleAction = (actionKey, group) => {
    switch (actionKey) {
      case 'edit':
        if (props.onEditGroup) {
          props.onEditGroup(group);
        }
        break;
      case 'delete':
        if (props.onDeleteGroup) {
          props.onDeleteGroup(group);
        }
        break;
      default:
        break;
    }
  };

  const formatAddress = (property) => {
    const addressParts = [
      property.address,
      property.city,
      property.state,
      property.zip
    ].filter(part => part && part.trim() !== '');
    
    return addressParts.join(', ') || 'Address not available';
  };

  const handleActiveFilter = (status) => {
    setActiveFilter(status);
    const newParams = {
      ...searchParams,
      active: status || '',
      page: 1, // Reset to first page on filetr change
    };
    setSearchParams(newParams);
    props.getPropertyGroups(newParams);
  };

  const handleRemoteFilter = (isRemote) => {
    setRemoteFilter(isRemote);
    const newParams = {
      ...searchParams,
      isRemote: isRemote || '',
      page: 1, // Reset to first page on filetr change
    };
    setSearchParams(newParams);
    props.getPropertyGroups(newParams);
  };

  const handleChangePage = (event, newPage) => {
    const newParams = {
      ...searchParams,
      page: newPage + 1, // MUI TablePagination uses 0-based index, backend uses 1-based
    };
    setSearchParams(newParams);
    props.getPropertyGroups(newParams);
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    const newParams = {
      ...searchParams,
      limit: newLimit,
      page: 1, // Reset to first page when changing rows per page
    };
    setSearchParams(newParams);
    props.getPropertyGroups(newParams);
  };

  const renderExpandedContent = (group) => {
    const properties = group.properties || group.propertyList || group.property_list || group.items || [];

    return (
      <Table size="small" aria-label="properties">
        <TableHead>
          <TableRow>
            <TableCell>Property Name</TableCell>
            <TableCell>Address</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {properties && properties.length > 0 ? (
            properties.map((property) => (
              <TableRow key={property.id || property.uuid}>
                <TableCell component="th" scope="row">
                  {property.name}
                </TableCell>
                <TableCell>{formatAddress(property)}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={2} align="center">
                No properties found in this group
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    );
  };

  const columns = [
    {
      field: 'name',
      label: 'Name',
      type: 'chip',
      render: (group) => (
        <Chip
          label={group.name}
          onClick={(e) => {
            e.stopPropagation();
            handleGroupChipClick(group);
          }}
          style={{
            backgroundColor: group.bgColorTag ? `#${group.bgColorTag}` : 'transparent',
            color: group.bgColorTag ? '#ffffff' : '#000000',
            border: group.bgColorTag ? 'none' : '1px solid #ccc',
            cursor: 'pointer'
          }}
          variant={group.bgColorTag ? "filled" : "outlined"}
          size="small"
        />
      )
    },
    {
      field: 'description',
      label: 'Description',
      type: 'text'
    },
    {
      field: 'isRemote',
      label: 'Type',
      type: 'chip',
      render: (group) => (
        <Chip
          label={group.isRemote ? "Remote" : "Field"}
          color={group.isRemote ? "secondary" : "default"}
          size="small"
        />
      )
    },
    {
      field: 'properties',
      label: 'Properties',
      type: 'count'
    },
    {
      field: 'active',
      label: 'Status',
      type: 'status'
    },
    {
      field: 'createdAt',
      label: 'Created',
      type: 'date',
      render: (group) => {
        const date = group.createdAt || group.created;
        if (!date) return 'N/A';
        if (typeof date === 'string') {
          if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
            return moment(date, 'YYYY-MM-DD').format("L");
          }
          if (/^\d{4}-\d{2}-\d{2}[\sT]/.test(date)) {
            return moment.utc(date).format("L");
          }
        }
        return moment.utc(date).format("L");
      }
    }
  ];


  const actions = [
    {
      key: 'edit',
      icon: EditIcon,
      tooltip: 'Edit group',
      color: 'primary'
    },
    {
      key: 'delete',
      icon: DeleteIcon,
      tooltip: (group) => {
        const properties = group.properties || group.propertyList || group.property_list || group.items || [];
        const propertyCount = Array.isArray(properties) ? properties.length : 0;
        return propertyCount > 0 ? "Remove all properties before deleting group" : "Delete group";
      },
      color: 'error',
      disabled: (group) => {
        const properties = group.properties || group.propertyList || group.property_list || group.items || [];
        const propertyCount = Array.isArray(properties) ? properties.length : 0;
        return propertyCount > 0;
      }
    }
  ];

  const data = props.properties?.propertyGroups?.items || [];
  const isLoading = props.properties?.propertyGroups?.isLoading || false;
  const error = props.properties?.propertyGroups?.error;

  if (error) {
    return (
      <Box p={3} textAlign="center">
        <Typography color="error">
          Error loading property groups: {error.message}
        </Typography>
      </Box>
    );
  }

  const getActiveChipStyle = (status) => {
    if (activeFilter === status) {
      switch (status) {
        case "true":
          return {
            backgroundColor: "rgba(76, 175, 80, 0.1)",
            color: "#4caf50 !important",
            border: "1px solid #4caf50",
            fontWeight: 500,
            borderRadius: "16px",
            fontSize: "0.875rem",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "rgba(76, 175, 80, 0.2)",
              border: "1px solid #4caf50",
            }
          };
        case "false":
          return {
            backgroundColor: "rgba(255, 193, 7, 0.1)",
            color: "#ff9800 !important",
            border: "1px solid #ff9800",
            fontWeight: 500,
            borderRadius: "16px",
            fontSize: "0.875rem",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "rgba(255, 193, 7, 0.2)",
              border: "1px solid #ff9800",
            }
          };
        case null:
          return {
            backgroundColor: "rgba(76, 175, 80, 0.1)",
            color: "#4caf50 !important",
            border: "1px solid #4caf50",
            fontWeight: 500,
            borderRadius: "16px",
            fontSize: "0.875rem",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "rgba(76, 175, 80, 0.2)",
              border: "1px solid #4caf50",
            }
          };
        default:
          return {
            backgroundColor: "rgba(158, 158, 158, 0.1)",
            color: "#757575 !important",
            border: "1px solid #bdbdbd",
            fontWeight: 500,
            borderRadius: "16px",
            fontSize: "0.875rem",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "rgba(158, 158, 158, 0.2)",
              border: "1px solid #bdbdbd",
            }
          };
      }
    }
    return {
      backgroundColor: "rgba(158, 158, 158, 0.1)",
      color: "#757575 !important",
      border: "1px solid #bdbdbd",
      fontWeight: 500,
      borderRadius: "16px",
      fontSize: "0.875rem",
      textTransform: "none",
      "&:hover": {
        backgroundColor: "rgba(158, 158, 158, 0.2)",
        border: "1px solid #bdbdbd",
      }
    };
  };

  const getRemoteChipStyle = (isRemote) => {
    if (remoteFilter === isRemote) {
      switch (isRemote) {
        case "true":
          return {
            backgroundColor: "rgba(33, 150, 243, 0.1)",
            color: "#2196f3 !important",
            border: "1px solid #2196f3",
            fontWeight: 500,
            borderRadius: "16px",
            fontSize: "0.875rem",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "rgba(33, 150, 243, 0.2)",
              border: "1px solid #2196f3",
            }
          };
        case "false":
          return {
            backgroundColor: "rgba(156, 39, 176, 0.1)",
            color: "#9c27b0 !important",
            border: "1px solid #9c27b0",
            fontWeight: 500,
            borderRadius: "16px",
            fontSize: "0.875rem",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "rgba(156, 39, 176, 0.2)",
              border: "1px solid #9c27b0",
            }
          };
        case null:
          return {
            backgroundColor: "rgba(76, 175, 80, 0.1)",
            color: "#4caf50 !important",
            border: "1px solid #4caf50",
            fontWeight: 500,
            borderRadius: "16px",
            fontSize: "0.875rem",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "rgba(76, 175, 80, 0.2)",
              border: "1px solid #4caf50",
            }
          };
        default:
          return {
            backgroundColor: "rgba(158, 158, 158, 0.1)",
            color: "#757575 !important",
            border: "1px solid #bdbdbd",
            fontWeight: 500,
            borderRadius: "16px",
            fontSize: "0.875rem",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "rgba(158, 158, 158, 0.2)",
              border: "1px solid #bdbdbd",
            }
          };
      }
    }
    return {
      backgroundColor: "rgba(158, 158, 158, 0.1)",
      color: "#757575 !important",
      border: "1px solid #bdbdbd",
      fontWeight: 500,
      borderRadius: "16px",
      fontSize: "0.875rem",
      textTransform: "none",
      "&:hover": {
        backgroundColor: "rgba(158, 158, 158, 0.2)",
        border: "1px solid #bdbdbd",
      }
    };
  };

  const ActiveFilters = () => {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Button
          onClick={() => handleActiveFilter(null)}
          sx={getActiveChipStyle(null)}
          variant="outlined"
          size="small"
        >
          All Status
        </Button>
        <Button
          onClick={() => handleActiveFilter('true')}
          sx={getActiveChipStyle('true')}
          variant="outlined"
          size="small"
        >
          Active
        </Button>
        <Button
          onClick={() => handleActiveFilter('false')}
          sx={getActiveChipStyle('false')}
          variant="outlined"
          size="small"
        >
          Inactive
        </Button>
      </Box>
    );
  };

  const RemoteFilters = () => {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Button
          onClick={() => handleRemoteFilter(null)}
          sx={getRemoteChipStyle(null)}
          variant="outlined"
          size="small"
        >
          All Types
        </Button>
        <Button
          onClick={() => handleRemoteFilter('true')}
          sx={getRemoteChipStyle('true')}
          variant="outlined"
          size="small"
        >
          Remote
        </Button>
        <Button
          onClick={() => handleRemoteFilter('false')}
          sx={getRemoteChipStyle('false')}
          variant="outlined"
          size="small"
        >
          Field
        </Button>
      </Box>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#666' }}>
              Status
            </Typography>
            <ActiveFilters />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#666' }}>
              Type
            </Typography>
            <RemoteFilters />
          </Box>
        </Box>
      </Box>
      
      <MUITable
        data={data}
        columns={columns}
        searchConfig={{
          enabled: true,
          placeholder: "Search by any property group field",
          initialParams: searchParams
        }}
        expandableConfig={{
          enabled: true,
          renderExpandedContent,
          expandTriggerField: 'properties'
        }}
        actionsConfig={{
          enabled: true,
          actions
        }}
        loadingState={{
          isLoading,
          loadingText: "Loading property groups..."
        }}
        emptyState={{
          message: "No property groups found. Try adjusting your search criteria.",
          showWhenEmpty: true
        }}
        paginationConfig={{
          enabled: true,
          total: total,
          page: currentPage - 1, // Convert to 0-based index for MUI
          rowsPerPage: currentLimit,
          rowsPerPageOptions: [10, 25, 50, 100],
          onPageChange: handleChangePage,
          onRowsPerPageChange: handleChangeRowsPerPage,
          labelDisplayedRows: ({ from, to, count }) => `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
        }}
        onSearch={handleSearch}
        onAction={handleAction}
      />
    </Box>
  );
}

PropertyGroupTable.propTypes = {
  properties: PropTypes.object.isRequired,
  getPropertyGroups: PropTypes.func.isRequired,
  onEditGroup: PropTypes.func,
  onDeleteGroup: PropTypes.func,
};

function State(state) {
  return {
    properties: state.properties,
  };
}

export default connect(State, { getPropertyGroups })(PropertyGroupTable);